import React, { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Spin } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Title from 'antd/es/typography/Title';
import { APP_LOCAL_DATE_FORMAT, APP_LOCAL_DATE_FORMAT_PAYLOAD } from 'app/config/constants';
import { checkIntKrQualYear, getKrQualCheckInYear, reset } from 'app/entities/checkIn/checkIn-year.reducer';
import CommentComponent from 'app/entities/comment/comment';
import ObjectiveUpdate from 'app/entities/objective/form/objective-update';
import { EvaluationTypes } from 'app/shared/model/enum/evaluation-type.enum';
import { StatusType } from 'app/shared/model/enum/status.enum';
import { formatNumber, parseNumber } from 'app/shared/util/help';
import { displayPercent, percent } from 'app/shared/util/percent';
import { filterOption } from 'app/shared/util/select-filter';
import dayjs from 'dayjs';
import KRUpdate from '../../kr-update';
import ReportQualitativeResultView from '../view/reportQualitativeResult';

type IReportQualitativeResult = {
  handleClose: (isReload?: boolean) => void;
  krCheckIn?: any;
  handleNextData: () => void;
  setUpdateSuccess: (isReload?: boolean) => void;
  isReadOnly?: boolean;
};

export const ReportQualitativeResult = ({ handleClose, krCheckIn, handleNextData, setUpdateSuccess, isReadOnly }: IReportQualitativeResult) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const filters = useAppSelector(state => state.filters.orgCheckIn);
  const workStatusList = useAppSelector(state => state.common.workStatus);
  const entity = useAppSelector(state => state.checkInYear.entity);
  const isLoading = useAppSelector(state => state.checkInYear.loading);
  const isUpdating = useAppSelector(state => state.checkInYear.updating);

  // init state
  const [isShowCheckInMonth, setShowCheckInMonth] = useState(false);
  const [isShowCheckInQuarter, setShowCheckInQuarter] = useState(false);
  const [isOpenModalFormKr, setIsOpenModalFormKr] = useState(false);
  const [isOpenModalFormObjective, setIsOpenModalFormObjective] = useState(false);
  const [isOpenDrawerComment, setOpenDrawerComment] = useState(false);
  const [isShowModalQualitativeView, setShowModalQualitativeView] = useState(false);

  // watch values form
  const workStatusMonthValues = Form.useWatch('workStatusMonth', form);
  const workStatusQuarterValues = Form.useWatch('workStatusQuarter', form);
  const workStatusYearValues = Form.useWatch('workStatusYear', form);
  const progressionPointQuarterValue = Form.useWatch('progressionPointQuarter', form);
  const qualityPointQuarterValue = Form.useWatch('qualityPointQuarter', form);
  const progressionPointYearValue = Form.useWatch('progressionPointYear', form);
  const qualityPointYearValue = Form.useWatch('qualityPointYear', form);

  useEffect(() => {
    if (krCheckIn && krCheckIn.id && filters) {
      innitData();
    }
  }, [krCheckIn]);

  useEffect(() => {
    if (entity) {
      form.setFieldsValue({ reportScheduleName: entity?.reportScheduleName })
      if (entity?.krYear) {
        form.setFieldsValue({
          issue: entity?.krYear?.issue,
          note: entity?.krYear?.note,
          solution: entity?.krYear?.solution,
          progressionPointYear: displayPercent(entity?.krYear?.progressionPoint),
          qualityPointYear: displayPercent(entity?.krYear?.qualityPoint),
          ratingNoteYear: entity?.krYear?.ratingNote,
          unFinishNoteYear: entity?.krYear?.unFinishNote,
          completeNoteYear: entity?.krYear?.completeNote,
          workStatusYear: entity?.krYear?.workStatus,
          completedOnYear: entity?.krYear?.completedOn !== null ? dayjs(entity?.krYear?.completedOn) : null,
          expectedCompleteOnYear: entity?.krYear?.expectedCompleteOn != null ? dayjs(entity?.krYear?.expectedCompleteOn) : null,
        })
      }
      if (entity?.krQuarter) {
        if (entity?.krQuarter?.purpose) setShowCheckInQuarter(true)
        else setShowCheckInQuarter(false)
        form.setFieldsValue({
          progressionPointQuarter: displayPercent(entity?.krQuarter?.progressionPoint),
          qualityPointQuarter: displayPercent(entity?.krQuarter?.qualityPoint),
          ratingNoteQuarter: entity?.krQuarter?.ratingNote,
          unFinishNoteQuarter: entity?.krQuarter?.unFinishNote,
          completeNoteQuarter: entity?.krQuarter?.completeNote,
          workStatusQuarter: entity?.krQuarter?.workStatus,
          completedOnQuarter: entity?.krQuarter?.completedOn !== null ? dayjs(entity?.krQuarter?.completedOn) : null,
          expectedCompleteOnQuarter: entity?.krQuarter?.expectedCompleteOn != null ? dayjs(entity?.krQuarter?.expectedCompleteOn) : null,
        })
      }
      if (entity?.krMonth) {
        if (entity?.krMonth?.purpose && entity?.krMonth?.purpose) setShowCheckInMonth(true)
        else setShowCheckInMonth(false)
        form.setFieldsValue({
          ratingNoteMonth: entity?.krMonth?.ratingNote,
          unFinishNoteMonth: entity?.krMonth?.unFinishNote,
          completeNoteMonth: entity?.krMonth?.completeNote,
          workStatusMonth: entity?.krMonth?.workStatus,
          completedOnMonth: entity?.krMonth?.completedOn !== null ? dayjs(entity?.krMonth?.completedOn) : null,
          expectedCompleteOnMonth: entity?.krMonth?.expectedCompleteOn != null ? dayjs(entity?.krMonth?.expectedCompleteOn) : null,
        })
      }
    }
  }, [entity]);

  useEffect(() => {
    if (qualityPointQuarterValue !== null || progressionPointQuarterValue !== null) {
      const { evaluationMethods } = entity?.krQuarter || {};
      const includesProgress = evaluationMethods?.includes(EvaluationTypes.Progress);
      const includesQuality = evaluationMethods?.includes(EvaluationTypes.Quality);

      if (includesProgress && !includesQuality) {
        form.setFieldValue('finalPointQuarter', progressionPointQuarterValue);
      } else if (includesQuality && !includesProgress) {
        form.setFieldValue('finalPointQuarter', qualityPointQuarterValue);
      } else {
        form.setFieldValue('finalPointQuarter', (qualityPointQuarterValue + progressionPointQuarterValue) / 2);
      }
    }
  }, [qualityPointQuarterValue, progressionPointQuarterValue]);

  useEffect(() => {
    if (qualityPointYearValue !== null || progressionPointYearValue !== null) {
      const { evaluationMethods } = entity?.krYear || {};
      const includesProgress = evaluationMethods?.includes(EvaluationTypes.Progress);
      const includesQuality = evaluationMethods?.includes(EvaluationTypes.Quality);

      if (includesProgress && !includesQuality) {
        form.setFieldValue('finalPointYear', progressionPointYearValue);
      } else if (includesQuality && !includesProgress) {
        form.setFieldValue('finalPointYear', qualityPointYearValue);
      } else {
        form.setFieldValue('finalPointYear', (qualityPointYearValue + progressionPointYearValue) / 2);
      }
    }
  }, [qualityPointYearValue, progressionPointYearValue]);

  const onClose = (isReload = false) => {
    form.resetFields();
    handleClose(isReload);
  }

  const innitData = () => {
    if (filters.reportScheduleId && krCheckIn.id)
      dispatch(getKrQualCheckInYear({ krId: krCheckIn?.id, reportScheduleId: filters?.reportScheduleId }));
  };

  const handleSubmit = values => {
    const payload = handleMapperDataBeforeSubmit(values)
    dispatch(checkIntKrQualYear(payload)).then(response => {
      if (response && response.payload) {
        const result = (response.payload as any).data
        if (!result) return;
        dispatch(reset());
        form.resetFields();
        onClose(true);
      }
    })
  };

  const handleNextKr = () => {
    form
      .validateFields()
      .then(values => {
        const payload = handleMapperDataBeforeSubmit(values)
        dispatch(checkIntKrQualYear(payload))
          .then(response => {
            if (response && response.payload) {
              const result = (response.payload as any).data
              if (!result) return;
              dispatch(reset());
              setUpdateSuccess(true);
              form.resetFields();
              form.scrollToField('reportScheduleName');
              handleNextData();
            }
          })
          .catch(() => {
            return;
          });
      })
      .catch((err) => {
        return err;
      })
  };

  const handleMapperDataBeforeSubmit = (values) => {
    return {
      krId: krCheckIn?.id,
      krYear: {
        workStatus: values.workStatusYear,
        completeOn: values.completedOnYear != null ? dayjs(values.completedOnYear).format(APP_LOCAL_DATE_FORMAT_PAYLOAD) : null,
        expectedCompleteOn: values.expectedCompleteOnYear != null ? dayjs(values.expectedCompleteOnYear).format(APP_LOCAL_DATE_FORMAT_PAYLOAD) : null,
        completeNote: values.completeNoteYear,
        unFinishNote: values.unFinishNoteYear,
        progressionPoint: percent(values.progressionPointYear),
        qualityPoint: percent(values.qualityPointYear),
        ratingNote: values.ratingNoteYear,
        issue: values.issue,
        solution: values.solution,
        note: values.note
      },
      krQuarter: {
        workStatus: values.workStatusQuarter,
        completeOn: values.completedOnQuarter != null ? dayjs(values.completedOnQuarter).format(APP_LOCAL_DATE_FORMAT_PAYLOAD) : null,
        expectedCompleteOn: values.expectedCompleteOnQuarter != null ? dayjs(values.expectedCompleteOnQuarter).format(APP_LOCAL_DATE_FORMAT_PAYLOAD) : null,
        completeNote: values.completeNoteQuarter,
        unFinishNote: values.unFinishNoteQuarter,
        progressionPoint: percent(values.progressionPointQuarter),
        qualityPoint: percent(values.qualityPointQuarter),
        ratingNote: values.ratingNoteQuarter,
      },
      krMonth: {
        workStatus: values.workStatusMonth,
        completeOn: values.completedOnMonth != null ? dayjs(values.completedOnMonth).format(APP_LOCAL_DATE_FORMAT_PAYLOAD) : null,
        expectedCompleteOn: values.expectedCompleteOnMonth != null ? dayjs(values.expectedCompleteOnMonth).format(APP_LOCAL_DATE_FORMAT_PAYLOAD) : null,
        completeNote: values.completeNoteMonth,
        unFinishNote: values.unFinishNoteMonth
      }

    }
  }

  const handleEditKr = () => {
    setIsOpenModalFormKr(true);
  };

  const handleCloseModelKr = () => {
    setIsOpenModalFormKr(false);
  };

  const handleEditObjective = () => {
    setIsOpenModalFormObjective(true);
  };

  const handleCloseModelObjective = () => {
    setIsOpenModalFormObjective(false);
  };

  const handleShowDrawerComment = () => {
    setOpenDrawerComment(true);
  };

  const handleCloseDrawerComment = () => {
    setOpenDrawerComment(false);
  };

  const handleViewReportResult = (event) => {
    event.preventDefault();
    setShowModalQualitativeView(true);
  };

  const handleCloseModalQualitativeView = () => {
    setShowModalQualitativeView(false);
  };

  const handleCollapseMonth = (e) => {
    e.preventDefault();
    setShowCheckInMonth(!isShowCheckInMonth);
  }

  const handleCollapseQuarter = (e) => {
    e.preventDefault();
    setShowCheckInQuarter(!isShowCheckInQuarter);
  }

  return (
    <Spin delay={500} spinning={isLoading}>
      <Form ref={formRef} size="large" form={form} disabled={isReadOnly} className='fw-bold' onFinish={handleSubmit} layout="vertical">
        <Row>
          <Col md={20}>
            <Title className="fw-bold" level={2}>
              {`Báo cáo thực hiện KR ${filters?.accountableName ? filters.accountableName : ''}`}
            </Title>
          </Col>
          <Col md={1} />
          <Col md={3}><Button onClick={handleShowDrawerComment}>Ý kiến trao đổi</Button></Col>
        </Row>
        <Row>
          <Col md={5}>
            <Form.Item label="Kỳ kế hoạch">
              <Input className="w-100" disabled value={entity?.planPeriodName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={5}>
            <Form.Item initialValue={entity?.reportScheduleName} label="Kỳ báo cáo" name={'reportScheduleName'}>
              <Input size='large' readOnly />
            </Form.Item>
          </Col>
          {/* <Col md={1} />
          <Col>
            <div className="mt-4">
              <span className="cursor-pointer text-primary" onClick={() => handleEditKr()}>
                {`Xem chi tiết KR >`}
              </span>
              <br />
              <span className="cursor-pointer text-primary" onClick={() => handleEditObjective()}>
                {`Xem chi tiết mục tiêu >`}
              </span>
            </div>
          </Col> */}
        </Row>
        <Row>
          <Col md={11}>
            <Form.Item label={`Mục tiêu - ${entity?.objectiveInfo?.code}`}>
              <TextArea rows={1} className="w-100" disabled value={entity?.objectiveInfo?.purpose} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item label="Lĩnh vực">
              <Input className="w-100" disabled value={entity?.objectiveInfo?.fieldName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item label="Dự án">
              <Input className="w-100" disabled value={entity?.objectiveInfo?.projectName} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={4}>
            <Form.Item label="Mảng công việc">
              <Input className="w-100" disabled value={entity?.objectiveInfo?.workTypeName} size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Form.Item label="Mã">
              <Input className="w-100" disabled value={entity?.krYear?.code} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={8}>
            <Form.Item label={`KR năm ${entity?.krYear?.name}`}>
              <TextArea rows={1} className="w-100" disabled value={entity?.krYear?.purpose} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={5}>
            <Form.Item label="Ngày bắt đầu">
              <DatePicker
                className="w-100"
                disabled
                value={dayjs(entity?.krYear?.startDate)}
                placeholder="Chọn ngày bắt đầu"
                size="large"
                format={APP_LOCAL_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={6}>
            <Form.Item label="Thời hạn hoàn thành">
              <DatePicker
                className="w-100"
                disabled
                value={dayjs(entity?.krYear?.dueDate)}
                placeholder="Chọn thời hạn hoàn thành"
                size="large"
                format={APP_LOCAL_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* Tháng */}
        <hr />
        <Row>
          <Col className="fw-bold h3" md={11}>
            <span className='cursor-pointer' onClick={handleCollapseMonth}>Thực hiện {entity?.krMonth?.name}</span>
          </Col>
        </Row>
        {isShowCheckInMonth && (
          <>
            <Row>
              <Col md={11}>
                <Form.Item label={`Kết quả cần đạt được`}>
                  <TextArea rows={1} className="w-100" disabled value={entity?.krMonth?.purpose} size="large" />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={5}>
                <Form.Item label="Ngày bắt đầu">
                  <DatePicker
                    className="w-100"
                    disabled
                    value={dayjs(entity?.krMonth?.startDate)}
                    placeholder="Chọn ngày bắt đầu"
                    size="large"
                    format={APP_LOCAL_DATE_FORMAT}
                  />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={6}>
                <Form.Item label="Thời hạn hoàn thành">
                  <DatePicker
                    className="w-100"
                    disabled
                    value={dayjs(entity?.krMonth?.dueDate)}
                    placeholder="Chọn thời hạn hoàn thành"
                    size="large"
                    format={APP_LOCAL_DATE_FORMAT}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={7}>
                <Form.Item label="Tình trạng"
                  rules={[{ required: entity?.krMonth?.purpose ? true : false, message: 'Hãy chọn tình trạng' }]}
                  name="workStatusMonth">
                  <Select
                    filterOption={filterOption}
                    allowClear
                    showSearch
                    size="large"
                    options={workStatusList}
                    placeholder="Chọn tình trạng"
                  />
                </Form.Item>
              </Col>
              <Col md={1} />
              {workStatusMonthValues === StatusType.Completed ? (
                <Col md={7}>
                  <Form.Item
                    label={`Ngày hoàn thành`}
                    rules={[
                      {
                        required: workStatusMonthValues === StatusType.Completed,
                        message: `Hãy chọn ngày hoàn thành`,
                      },
                    ]}
                    name={`completedOnMonth`}
                  >
                    <DatePicker
                      size="large"
                      format={APP_LOCAL_DATE_FORMAT} placeholder={`chọn ngày hoàn thành`} className="w-100" />
                  </Form.Item>
                </Col>
              ) : undefined}
              {(workStatusMonthValues === StatusType.NotCompleted || workStatusMonthValues === StatusType.OnTrack) ? (
                <Col md={7}>
                  <Form.Item
                    label={`Ngày hoàn thành dự kiến`}
                    rules={[
                      {
                        required: workStatusMonthValues && (workStatusMonthValues === StatusType.NotCompleted || workStatusMonthValues === StatusType.OnTrack),
                        message: `Hãy chọn ngày hoàn thành dự kiến`,
                      },
                    ]}
                    name={`expectedCompleteOnMonth`}
                  >
                    <DatePicker
                      size="large" format={APP_LOCAL_DATE_FORMAT} placeholder={`chọn ngày hoàn thành dự kiến`} className="w-100" />
                  </Form.Item>
                </Col>
              ) : undefined}
            </Row>
            <Row>
              <Col md={11}>
                <Form.Item
                  label="Đã hoàn thành"
                  rules={[
                    {
                      required: workStatusMonthValues === StatusType.Completed,
                      message: `Hãy nhập nội dung đã hoàn thành`,
                    },
                  ]}
                  name={'completeNoteMonth'}
                >
                  <TextArea rows={6} placeholder="Nhập nội dung đã hoàn thành" size="large" />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={12}>
                <Form.Item
                  label="Lý do không hoàn thành, Tạm dừng, Hủy"
                  rules={[
                    {
                      required: workStatusMonthValues && workStatusMonthValues !== StatusType.Completed,
                      message: `Hãy nhập lý do`,
                    },
                  ]}
                  name={'unFinishNoteMonth'}
                >
                  <TextArea rows={6} placeholder="Nhập lý do" size="large" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        {/* Quý */}
        <hr />
        <Row>
          <Col className="fw-bold h3" md={5}>
            <span className='cursor-pointer' onClick={handleCollapseQuarter}>Thực hiện {entity?.krQuarter?.name}</span>
          </Col>
          <Col md={12}><span onClick={handleViewReportResult} className='text-primary cursor-pointer fw-bold'>Xem kết quả thực hiện KR</span></Col>
        </Row>

        {isShowCheckInQuarter && (
          <>
            <Row>
              <Col md={11}>
                <Form.Item label={`Kết quả cần đạt được`}>
                  <TextArea rows={1} className="w-100" disabled value={entity?.krQuarter?.purpose} size="large" />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={5}>
                <Form.Item label="Ngày bắt đầu">
                  <DatePicker
                    className="w-100"
                    disabled
                    value={dayjs(entity?.krQuarter?.startDate)}
                    placeholder="Chọn ngày bắt đầu"
                    size="large"
                    format={APP_LOCAL_DATE_FORMAT}
                  />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={6}>
                <Form.Item label="Thời hạn hoàn thành">
                  <DatePicker
                    className="w-100"
                    disabled
                    value={dayjs(entity?.krQuarter?.dueDate)}
                    placeholder="Chọn thời hạn hoàn thành"
                    size="large"
                    format={APP_LOCAL_DATE_FORMAT}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={7}>
                <Form.Item label="Tình trạng"
                  rules={[{ required: entity?.krQuarter?.purpose ? true : false, message: 'Hãy chọn tình trạng' }]}
                  name="workStatusQuarter">
                  <Select
                    filterOption={filterOption}
                    allowClear
                    showSearch
                    size="large"
                    options={workStatusList}
                    placeholder="Chọn tình trạng"
                  />
                </Form.Item>
              </Col>
              <Col md={1} />
              {workStatusQuarterValues === StatusType.Completed ? (
                <Col md={7}>
                  <Form.Item
                    label={`Ngày hoàn thành`}
                    rules={[
                      {
                        required: workStatusQuarterValues === StatusType.Completed,
                        message: `Hãy chọn ngày hoàn thành`,
                      },
                    ]}
                    name={`completedOnQuarter`}
                  >
                    <DatePicker
                      size="large"
                      format={APP_LOCAL_DATE_FORMAT} placeholder={`chọn ngày hoàn thành`} className="w-100" />
                  </Form.Item>
                </Col>
              ) : undefined}
              {(workStatusQuarterValues === StatusType.NotCompleted || workStatusQuarterValues === StatusType.OnTrack) ? (
                <Col md={7}>
                  <Form.Item
                    label={`Ngày hoàn thành dự kiến`}
                    rules={[
                      {
                        required: workStatusQuarterValues && (workStatusQuarterValues === StatusType.NotCompleted || workStatusQuarterValues === StatusType.OnTrack),
                        message: `Hãy chọn ngày hoàn thành dự kiến`,
                      },
                    ]}
                    name={`expectedCompleteOnQuarter`}
                  >
                    <DatePicker
                      size="large" format={APP_LOCAL_DATE_FORMAT} placeholder={`chọn ngày hoàn thành dự kiến`} className="w-100" />
                  </Form.Item>
                </Col>
              ) : undefined}
            </Row>
            <Row>
              <Col md={11}>
                <Form.Item
                  label="Đã hoàn thành"
                  rules={[
                    {
                      required: workStatusQuarterValues === StatusType.Completed,
                      message: `Hãy nhập nội dung đã hoàn thành`,
                    },
                  ]}
                  name={'completeNoteQuarter'}
                >
                  <TextArea rows={6} placeholder="Nhập nội dung đã hoàn thành" size="large" />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={12}>
                <Form.Item
                  label="Lý do không hoàn thành, Tạm dừng, Hủy"
                  rules={[
                    {
                      required: workStatusQuarterValues && workStatusQuarterValues !== StatusType.Completed,
                      message: `Hãy nhập lý do`,
                    },
                  ]}
                  name={'unFinishNoteQuarter'}
                >
                  <TextArea rows={6} placeholder="Nhập lý do" size="large" />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col className="fw-bold" md={24}>
                Đánh giá tỷ lệ hoàn thành
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Form.Item
                  label="Tiến độ"
                  rules={[
                    {
                      required:
                        entity?.krQuarter?.purpose && (workStatusQuarterValues === StatusType.Completed || workStatusQuarterValues === StatusType.NotCompleted) &&
                        entity?.krQuarter?.evaluationMethods?.includes(EvaluationTypes.Progress),
                      message: 'Vui lòng nhập',
                    },
                  ]}
                  name={'progressionPointQuarter'}
                >
                  <InputNumber
                    formatter={formatNumber}
                    parser={parseNumber}
                    className="w-100"
                    disabled={!entity?.krQuarter?.evaluationMethods?.includes(EvaluationTypes.Progress)}
                    placeholder="Nhập tiến độ"
                    size="large"
                    addonAfter="%"
                  />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={3}>
                <Form.Item
                  label="Chất lượng"
                  rules={[
                    {
                      required:
                        entity?.krQuarter?.purpose && (workStatusQuarterValues === StatusType.Completed || workStatusQuarterValues === StatusType.NotCompleted) &&
                        entity?.krQuarter?.evaluationMethods?.includes(EvaluationTypes.Quality),
                      message: 'Vui lòng nhập',
                    },
                  ]}
                  name={'qualityPointQuarter'}
                >
                  <InputNumber
                    formatter={formatNumber}
                    parser={parseNumber}
                    className="w-100"
                    disabled={!entity?.krQuarter?.evaluationMethods?.includes(EvaluationTypes.Quality)}
                    placeholder="Nhập chất lượng"
                    size="large"
                    addonAfter="%"
                  />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={3}>
                <Form.Item label="Trung bình" initialValue={0} name={'finalPointQuarter'}>
                  <InputNumber min={0} formatter={formatNumber} parser={parseNumber} className="w-100" disabled size="large" addonAfter="%" />
                </Form.Item>
              </Col>
              <Col md={1} />
              <Col md={12}>
                <Form.Item label="Ghi chú" name={'ratingNoteQuarter'}>
                  <Input className="w-100" placeholder="Nhập ghi chú" size="large" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        {/* Năm */}
        <hr />
        <Row>
          <Col className="fw-bold h3" md={6}>
            Thực hiện {entity?.krYear?.name}
          </Col>
          <Col md={12}><span onClick={handleViewReportResult} className='text-primary cursor-pointer fw-bold'>Xem kết quả thực hiện KR</span></Col>
        </Row>
        <Row>
          <Col md={11}>
            <Form.Item label={`KR năm`}>
              <TextArea rows={1} className="w-100" disabled value={entity?.krYear?.purpose} size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={5}>
            <Form.Item label="Ngày bắt đầu">
              <DatePicker
                className="w-100"
                disabled
                value={dayjs(entity?.krYear?.startDate)}
                placeholder="Chọn ngày bắt đầu"
                size="large"
                format={APP_LOCAL_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={6}>
            <Form.Item label="Thời hạn hoàn thành">
              <DatePicker
                className="w-100"
                disabled
                value={dayjs(entity?.krYear?.dueDate)}
                placeholder="Chọn thời hạn hoàn thành"
                size="large"
                format={APP_LOCAL_DATE_FORMAT}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col md={7}>
            <Form.Item label="Tình trạng"
              rules={[{ required: entity?.krYear?.purpose ? true : false, message: 'Hãy chọn tình trạng' }]}
              name="workStatusYear">
              <Select
                filterOption={filterOption}
                allowClear
                showSearch
                size="large"
                options={workStatusList}
                placeholder="Chọn tình trạng"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          {workStatusYearValues === StatusType.Completed ? (
            <Col md={7}>
              <Form.Item
                label={`Ngày hoàn thành`}
                rules={[
                  {
                    required: workStatusYearValues === StatusType.Completed,
                    message: `Hãy chọn ngày hoàn thành`,
                  },
                ]}
                name={`completedOnYear`}
              >
                <DatePicker
                  size="large"
                  format={APP_LOCAL_DATE_FORMAT} placeholder={`chọn ngày hoàn thành`} className="w-100" />
              </Form.Item>
            </Col>
          ) : undefined}
          {(workStatusYearValues === StatusType.NotCompleted || workStatusYearValues === StatusType.OnTrack) ? (
            <Col md={7}>
              <Form.Item
                label={`Ngày hoàn thành dự kiến`}
                rules={[
                  {
                    required: workStatusYearValues && (workStatusYearValues === StatusType.NotCompleted || workStatusYearValues === StatusType.OnTrack),
                    message: `Hãy chọn ngày hoàn thành dự kiến`,
                  },
                ]}
                name={`expectedCompleteOnYear`}
              >
                <DatePicker
                  size="large" format={APP_LOCAL_DATE_FORMAT} placeholder={`chọn ngày hoàn thành dự kiến`} className="w-100" />
              </Form.Item>
            </Col>
          ) : undefined}
        </Row>
        <Row>
          <Col md={11}>
            <Form.Item
              label="Đã hoàn thành"
              rules={[
                {
                  required: workStatusYearValues === StatusType.Completed,
                  message: `Hãy nhập nội dung đã hoàn thành`,
                },
              ]}
              name={'completeNoteYear'}
            >
              <TextArea rows={6} placeholder="Nhập nội dung đã hoàn thành" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item
              label="Lý do không hoàn thành, Tạm dừng, Hủy"
              rules={[
                {
                  required: workStatusYearValues && workStatusYearValues !== StatusType.Completed,
                  message: `Hãy nhập lý do`,
                },
              ]}
              name={'unFinishNoteYear'}
            >
              <TextArea rows={6} placeholder="Nhập lý do" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col className="fw-bold" md={24}>
            Đánh giá tỷ lệ hoàn thành
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <Form.Item
              label="Tiến độ"
              rules={[
                {
                  required:
                    entity?.krYear?.purpose && (workStatusYearValues === StatusType.Completed || workStatusYearValues === StatusType.NotCompleted) &&
                    entity?.krYear?.evaluationMethods?.includes(EvaluationTypes.Progress),
                  message: 'Vui lòng nhập',
                },
              ]}
              name={'progressionPointYear'}
            >
              <InputNumber
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                disabled={!entity?.krYear?.evaluationMethods?.includes(EvaluationTypes.Progress)}
                placeholder="Nhập tiến độ"
                size="large"
                addonAfter="%"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item
              label="Chất lượng"
              rules={[
                {
                  required:
                    entity?.krYear?.purpose && (workStatusYearValues === StatusType.Completed || workStatusYearValues === StatusType.NotCompleted) &&
                    entity?.krYear?.evaluationMethods?.includes(EvaluationTypes.Quality),
                  message: 'Vui lòng nhập',
                },
              ]}
              name={'qualityPointYear'}
            >
              <InputNumber
                formatter={formatNumber}
                parser={parseNumber}
                className="w-100"
                disabled={!entity?.krYear?.evaluationMethods?.includes(EvaluationTypes.Quality)}
                placeholder="Nhập chất lượng"
                size="large"
                addonAfter="%"
              />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={3}>
            <Form.Item label="Trung bình" initialValue={0} name={'finalPointYear'}>
              <InputNumber min={0} formatter={formatNumber} parser={parseNumber} className="w-100" disabled size="large" addonAfter="%" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item label="Ghi chú" name={'ratingNoteYear'}>
              <Input className="w-100" placeholder="Nhập ghi chú" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col md={11}>
            <Form.Item label="Khó khăn & nguyên nhân" name={'issue'}>
              <TextArea rows={6} placeholder="Nhập khó khăn & nguyên nhân" size="large" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item label="Giải pháp và kiến nghị" name={'solution'}>
              <TextArea rows={6} placeholder="Nhập giải pháp và kiến nghị" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col md={24}>
            <Form.Item label="Tài liệu đính kèm về kết quả thực hiện (link)" name={'note'}>
              <TextArea placeholder="Nhập tài liệu đính kèm về kết quả thực hiện" size="large" />
            </Form.Item>
            <Form.Item valuePropName="checked" name={'sendingNotification'}>
              <Checkbox>Gửi thông báo đến người nhận</Checkbox>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Row className="d-flex gap-3 mt-3" justify="end">
            {!isReadOnly &&
              <>
                <Col>
                  <Button
                    className="green"
                    onClick={() => {
                      handleNextKr();
                    }}
                    disabled={isUpdating}
                  >
                    Lưu & Chuyển sang check in KR khác
                  </Button>
                </Col>
                <Col>
                  <Button className="primary" htmlType="submit" disabled={isUpdating}>
                    Lưu
                  </Button>
                </Col>
                <Col>
                  <Button
                    className="secondary"
                    onClick={() => {
                      handleNextData();
                    }}
                  >
                    Bỏ qua
                  </Button>
                </Col>
              </>
            }
            <Col>
              <Button
                className="secondary"
                onClick={() => {
                  onClose();
                }}
              >
                Hủy
              </Button>
            </Col>
          </Row>
        </Form.Item>
        {isOpenModalFormObjective && (
          <ObjectiveUpdate isOpen={isOpenModalFormObjective} id={entity?.objective?.id} onClose={handleCloseModelObjective} />
        )}
        {isOpenModalFormKr && (
          <KRUpdate
            isOpen={isOpenModalFormKr}
            idKrBaseSelected={krCheckIn?.id}
            idObjective={entity?.objective?.id}
            planPeriodType={filters?.planPeriodType}
            krTypeRequest={krCheckIn?.krType}
            planPeriodIdSelected={filters?.planPeriodId}
            onClose={handleCloseModelKr}
          />
        )}
        {isShowModalQualitativeView && (
          <ReportQualitativeResultView
            isOpen={isShowModalQualitativeView}
            idKrSelected={krCheckIn?.id}
            onClose={handleCloseModalQualitativeView}
          />
        )}
        {isOpenDrawerComment &&
          <CommentComponent topicId={krCheckIn?.id} handleDrawerComment={handleCloseDrawerComment} isOpenDrawerComment={isOpenDrawerComment} />
        }
      </Form>
    </Spin>
  );
};

export default ReportQualitativeResult;
